<template>
  <NuxtLayout name="standard">
    <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
    <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />

    <template v-if="holidays">
      <SSection v-if="hero" :has-separator="false" :mobile-full="true">
        <SHeroCardSearch v-bind="hero" />
      </SSection>

      <SSection
        v-if="shortDescription"
        :has-separator="false"
        :full-width="false"
        :centered="true"
      >
        <SShortDescription v-bind="shortDescription" />
      </SSection>

      <template v-for="(item, index) in swipers">
        <SSection
          v-if="item"
          :key="index"
          :title="item?.title"
          :has-separator="false"
        >
          <template #no-container>
            <SCottageSwiper
              v-bind="item.sCottageSwiperProps"
              @swiper-click="url => clickSwiperSection(item)"
              @button-click="url => clickSwiperSection(item)"
            />
          </template>
        </SSection>
      </template>

      <SSection v-if="ctaCard" :has-separator="false" :mobile-full="true">
        <SCtaCard v-bind="ctaCard" />
      </SSection>

      <SSectionColor
        v-if="dataPosts"
        :title="holidays.attributes.blog_posts?.title"
        background-color="gray-50"
      >
        <SBlogposts v-bind="dataPosts" />
      </SSectionColor>

      <SSection
        v-if="longDescription"
        :has-separator="false"
        :full-width="false"
        :centered="true"
        :title="holidays.attributes.long_description?.title"
      >
        <SLongDescription v-bind="longDescription" />
      </SSection>

      <SSection
        v-if="reviews && reviews.items.length > 0"
        :title="
          t('landings.holidays.reviews.title', {
            name: holidays.attributes.name,
          })
        "
      >
        <SReviews v-bind="reviews" variant="default" />
      </SSection>

      <SSection v-if="seoLinks" :title="t('landings.holidays.links.title')">
        <SLinks v-bind="seoLinks"></SLinks>
      </SSection>

      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Types
import type { schemaType as Holidays } from '~~/shared/api/strapi/vacaciones-landings/detail/schema'
import type { propsInterface as SBlogPostsPropsInterface } from '~~/modules/design-system/sections/s-blogposts.vue'

// Composables
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'
import { sendGa4HolidaysClickEvent } from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useHeadSchemaComposable } from '~~/modules/head/composables/useHeadSchemaComposable'

// Apis
import { serverFetch as getHolidaysList } from '~~/modules/api/strapi/vacaciones-landings/list'
import { serverFetch as getHolidaysDetail } from '~~/modules/api/strapi/vacaciones-landings/detail'
import { serverFetch as getBlogPosts } from '~~/modules/api/rurality/v3/blog/posts/list'
import { serverFetch as getCottagesList } from '~~/modules/api/rurality/v3/cottages/list'

import { serverFetch as getCottageReviews } from '~~/modules/api/rurality/v3/cottage-reviews/province/detail'
import { serverFetch as getCottageReviewsSummary } from '~~/modules/api/rurality/v3/cottage-reviews/province/summary'

// Transformers
import { useToSBlogposts } from '~/composables/transformers/useToSBlogposts'
import { useToSLinks } from '~/composables/transformers/useToSLinks'
import { useToSCottageSwiper } from '~/composables/transformers/useToSCottageSwiper'
import { useToSShortDescription } from '~/composables/transformers/useToSShortDescription'
import { useToSLongDescription } from '~/composables/transformers/useToSLongDescription'
import { useToSHeroCardSearch } from '~/composables/transformers/useToSHeroCardSearch'
import { useToSCtaCard } from '~/composables/transformers/useToSCtaCard'
import { useToSReviews } from '~/composables/transformers/useToSReviews'
import { useToProduct } from '~~/modules/head/composables/transformers/useToProduct'

// Components Props
import type { propsInterface as sCottagesSwiperProps } from '~~/modules/design-system/sections/s-cottage-swiper.vue'
import type { propsInterface as sLongDescriptionProps } from '~~/modules/design-system/sections/s-long-description.vue'
import type { propsInterface as sShortDescriptionProps } from '~~/modules/design-system/sections/s-short-description/s-short-description.vue'
import type { propsInterface as sHeroCardSearchProps } from '~~/modules/design-system/sections/s-hero-card-search.vue'
import type { propsInterface as sCtaCardProps } from '~~/modules/design-system/sections/s-cta-card/s-cta-card.vue'
import type { propsInterface as sLinksProps } from '~~/modules/design-system/sections/s-links/s-links.vue'
import type { propsInterface as sReviewsProps } from '~~/modules/design-system/sections/s-reviews.vue'

const route = useRoute()

const { setHeadInfo } = useHeadInfoComposable()

const { productSchema } = useHeadSchemaComposable()

type sectionsCottageSwiperType = {
  title: string
  sCottageSwiperProps: sCottagesSwiperProps
}

const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error?.message,
    fatal: true,
  })
}

const showErrorNotFound = () => {
  throw createError({
    statusCode: 404,
    fatal: true,
  })
}

const swipers: sectionsCottageSwiperType[] = []

let holidays: Holidays
let shortDescription: sShortDescriptionProps
let longDescription: sLongDescriptionProps
let hero: sHeroCardSearchProps
let ctaCard: sCtaCardProps
let dataPosts: SBlogPostsPropsInterface
let seoLinks: sLinksProps
let reviews: sReviewsProps

const dataApiDetail = await getHolidaysDetail({
  slug: route.params.slug as string,
})

if (
  dataApiDetail.success &&
  dataApiDetail.success.data.attributes.publishedAt
) {
  holidays = dataApiDetail.success.data as Holidays
  setHeadInfo({
    title: t('landings.holidays.seo.title', { name: holidays.attributes.name }),
    description: t('landings.holidays.seo.description', {
      name: holidays.attributes.name,
    }),
    robotsVisible: true,
    images: [
      {
        url: holidays.attributes.img?.data?.attributes.url as string,
        alt: t('landings.holidays.seo.title', {
          name: holidays.attributes.name,
        }),
      },
    ],
  })

  const breadCrumbMenuStore = useMenuBreadcrumbStore()
  breadCrumbMenuStore.setMenuBreadcrumb(holidays.attributes.name, [
    {
      href: routes().holidaysList(),
      name: t('menu.breadcrumb.holidays'),
    },
  ])

  shortDescription = {
    text: useToSShortDescription().fromStrapiHolidaysDetail(holidays),
  }
  longDescription = {
    text: useToSLongDescription().fromStrapiHolidaysDetail(holidays),
  }
  hero = useToSHeroCardSearch().fromStrapiHolidaysDetail(holidays)
  ctaCard = useToSCtaCard().fromStrapiHolidaysDetail(holidays)

  if (holidays.attributes.cottages) {
    for (let i = 0; i < holidays.attributes.cottages.length; i++) {
      const cottage = holidays.attributes.cottages[i]
      const facets = cottage.facets

      const dataGetCottages = await getCottagesList({ facets })
      if (dataGetCottages?.success) {
        const propsReady = useToSCottageSwiper().fromCottagesList(
          dataGetCottages.success.data,
          cottage,
        )
        const cottagesData: sectionsCottageSwiperType = {
          title: cottage.title,
          sCottageSwiperProps: propsReady,
        }
        if (cottagesData) {
          swipers.push(cottagesData)
        }
      }
    }
  }

  const getPosts = await getBlogPosts({
    slugs: holidays.attributes?.blog_posts?.slugs as string,
  })
  if (getPosts.success && getPosts.success.data) {
    dataPosts = useToSBlogposts().fromBlogPostList(getPosts.success.data)
  }

  const dataApiList = await getHolidaysList()
  if (dataApiList.success) {
    seoLinks = useToSLinks().fromStrapiHolidaysList({
      dataIn: dataApiList.success.data,
      filterName: holidays.attributes.name,
    })
  }

  const dataGetCottageReviews = await getCottageReviews({
    province_slug: route.params.slug as string,
    limit: 6,
  })

  if (dataGetCottageReviews.success) {
    reviews = useToSReviews().fromRuralityCottageReviewsProvince({
      dataIn: dataGetCottageReviews.success.data,
      province_slug: route.params.slug as string,
    })

    const dataGetCottageReviewsSummary = await getCottageReviewsSummary({
      province_slug: route.params.slug as string,
    })

    if (dataGetCottageReviewsSummary.success) {
      const paramsProduct = useToProduct().fromStrapiHolidaysAndRuralityReviews(
        {
          strapiHolidays: holidays,
          ruralityReviews: dataGetCottageReviewsSummary.success.data,
          sku: route.params.slug as string,
        },
      )

      productSchema(paramsProduct)
    }
  }
} else if (
  dataApiDetail.success &&
  !dataApiDetail.success.data.attributes.publishedAt
) {
  showErrorNotFound()
} else if (dataApiDetail.error) {
  showErrorPage(dataApiDetail.error as ApiResponseError)
}

const clickSwiperSection = (itemEl: sectionsCottageSwiperType) => {
  sendGa4HolidaysClickEvent({
    province: route.params.slug as string,
    title: itemEl.title,
  })
}

sendConnectifPage()
</script>
